import React, { useState } from "react";
import {
  Box,
  Typography,
  Sheet,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Modal,
  ModalDialog,
  ModalClose,
  Stack,
  Tooltip,
  Card,
  CardContent,
  Grid,
} from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ArticleIcon from "@mui/icons-material/Article";
import BarChartIcon from "@mui/icons-material/BarChart";
import InputIcon from "@mui/icons-material/Input";
import { useNavigate } from "react-router-dom";
import { useWorkspaceContext } from "../../../providers/WorkspaceProvider";
import { useCanvases, useCanvasMutations } from "../hooks";
import { FloatingChatWidget } from "../components/MyVaultAgent";
import type { Canvas, CanvasType } from "../types";
import { canvasTypeFromDefinition } from "./canvasArchetype";
import "./CanvasBrowser.css";

const canvasTypeIcons: Record<CanvasType, React.ReactNode> = {
  entry: <InputIcon />,
  browse: <DashboardIcon />,
  report: <ArticleIcon />,
  dashboard: <BarChartIcon />,
  wizard: <ArticleIcon />,
};

const canvasTypeLabels: Record<CanvasType, string> = {
  entry: "Data Entry",
  browse: "Browse",
  report: "Report",
  dashboard: "Dashboard",
  wizard: "Wizard",
};

const canvasTypeColors: Record<
  CanvasType,
  "primary" | "success" | "warning" | "neutral"
> = {
  entry: "primary",
  browse: "neutral",
  report: "success",
  dashboard: "warning",
  wizard: "neutral",
};

/**
 * CanvasBrowser - Browse and discover canvas applications
 */
export default function CanvasBrowser() {
  const navigate = useNavigate();
  const { workspace, isEditor } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";

  const { canvases, loading, error, refresh } = useCanvases(workspaceId, {
    includeUnpublished: isEditor,
  });
  const { deleteCanvas, loading: deleting } = useCanvasMutations(workspaceId);

  const [deleteConfirm, setDeleteConfirm] = useState<Canvas | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    try {
      await deleteCanvas(deleteConfirm.id);
      setDeleteConfirm(null);
      setDeleteError(null);
      refresh();
    } catch (err) {
      setDeleteError(
        err instanceof Error ? err.message : "Failed to delete canvas",
      );
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (!workspaceId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="warning">Please select a workspace first.</Alert>
      </Box>
    );
  }

  const renderCanvasCard = (canvas: Canvas) => {
    const canvasType = canvasTypeFromDefinition(canvas);
    return (
      <Card
        key={canvas.id}
        className="myvault-app-card"
        data-testid={`canvas-card-${canvas.id}`}
        variant="outlined"
        sx={{
          height: 264,
          boxSizing: "border-box",
          cursor: "pointer",
          transition: "all 0.2s",
          "&:hover": {
            borderColor: "primary.500",
            boxShadow: "sm",
          },
        }}
        onClick={() => navigate(`${canvas.id}`)}
      >
        <CardContent
          sx={{
            height: "100%",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Stack spacing={1.25} sx={{ width: "100%", minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  p: 0.75,
                  borderRadius: "sm",
                  bgcolor: `${canvasTypeColors[canvasType]}.softBg`,
                  color: `${canvasTypeColors[canvasType]}.solidBg`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {canvasTypeIcons[canvasType]}
              </Box>
              <Chip
                size="sm"
                variant="soft"
                color={canvasTypeColors[canvasType]}
              >
                {canvasTypeLabels[canvasType]}
              </Chip>
            </Stack>

            <Typography
              level="title-md"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
              title={canvas.name}
            >
              {canvas.name}
            </Typography>

            <Typography
              level="body-sm"
              data-testid={`canvas-description-${canvas.id}`}
              sx={{
                color: "text.secondary",
                lineHeight: "20px",
                minHeight: "20px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={canvas.description || undefined}
            >
              {canvas.description || ""}
            </Typography>

            {isEditor && (canvas.status || canvas.elements) && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {canvas.status && (
                  <Chip
                    size="sm"
                    variant="soft"
                    color={
                      canvas.status === "published" ? "success" : "neutral"
                    }
                  >
                    {canvas.status}
                  </Chip>
                )}
                {canvas.elements && (
                  <Chip size="sm" variant="outlined" color="neutral">
                    {canvas.elements.length} elements
                  </Chip>
                )}
              </Stack>
            )}

            {isEditor && (
              <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                Created {formatDate(canvas.created_at)}
              </Typography>
            )}

            <Stack
              direction="row"
              spacing={0}
              className="myvault-card-actions"
              data-testid={`canvas-card-footer-${canvas.id}`}
              sx={{
                mt: "auto !important",
                pt: 1.25,
                flexWrap: "nowrap",
                borderTop: "1px solid",
                borderColor: "divider",
                gap: 1,
                "& .MuiButton-root": {
                  minWidth: 32,
                  flexShrink: 0,
                },
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Tooltip title="Open canvas">
                <Button
                  aria-label="Open canvas"
                  size="sm"
                  variant="soft"
                  color="success"
                  startDecorator={<PlayArrowIcon fontSize="small" />}
                  onClick={() => navigate(`${canvas.id}`)}
                >
                  <Box component="span" className="myvault-card-action-label">
                    Open
                  </Box>
                </Button>
              </Tooltip>
              {isEditor && (
                <Tooltip title="Edit canvas">
                  <Button
                    aria-label="Edit canvas"
                    size="sm"
                    variant="plain"
                    color="neutral"
                    startDecorator={<EditIcon fontSize="small" />}
                    onClick={() => navigate(`${canvas.id}/edit`)}
                  >
                    <Box component="span" className="myvault-card-action-label">
                      Edit
                    </Box>
                  </Button>
                </Tooltip>
              )}
              {isEditor && (
                <Tooltip title="Delete canvas">
                  <Button
                    aria-label="Delete canvas"
                    size="sm"
                    variant="plain"
                    color="danger"
                    startDecorator={<DeleteIcon fontSize="small" />}
                    onClick={() => setDeleteConfirm(canvas)}
                  >
                    <Box component="span" className="myvault-card-action-label">
                      Delete
                    </Box>
                  </Button>
                </Tooltip>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography level="h2">Apps</Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton
              variant="outlined"
              color="neutral"
              onClick={refresh}
              disabled={loading}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {isEditor && (
            <Button
              startDecorator={<AddIcon />}
              color="primary"
              onClick={() => navigate("create")}
            >
              New Canvas
            </Button>
          )}
        </Stack>
      </Box>

      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
          >
            <Typography level="body-sm">{error}</Typography>
            <Button
              size="sm"
              variant="soft"
              onClick={refresh}
              disabled={loading}
            >
              Retry
            </Button>
          </Stack>
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : canvases.length === 0 && !error ? (
        <Sheet
          variant="outlined"
          sx={{
            borderRadius: "md",
            p: 4,
            textAlign: "center",
          }}
        >
          <DashboardIcon sx={{ fontSize: 48, color: "text.tertiary", mb: 2 }} />
          <Typography level="body-lg" sx={{ color: "text.secondary" }}>
            {isEditor
              ? "No canvas apps created yet."
              : "No apps are available yet."}
          </Typography>
          <Typography level="body-sm" sx={{ color: "text.tertiary", mt: 1 }}>
            {isEditor
              ? "Build your first data entry form or dashboard."
              : "Ask a workspace administrator when an app will be published."}
          </Typography>
        </Sheet>
      ) : (
        <Grid container spacing={2}>
          {canvases.map((canvas) => (
            <Grid key={canvas.id} xs={12} sm={6} md={4} lg={3}>
              {renderCanvasCard(canvas)}
            </Grid>
          ))}
        </Grid>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        open={isEditor && !!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
      >
        <ModalDialog>
          <ModalClose />
          <Typography level="h4">Delete Canvas</Typography>
          <Typography level="body-md" sx={{ mt: 1 }}>
            Are you sure you want to delete the canvas &quot;
            {deleteConfirm?.name}&quot;?
          </Typography>
          <Typography level="body-sm" sx={{ color: "text.secondary", mt: 1 }}>
            This will remove the canvas definition. Data entered through this
            canvas will not be affected.
          </Typography>
          {deleteError && (
            <Alert color="danger" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 3, justifyContent: "flex-end" }}
          >
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </Button>
            <Button color="danger" onClick={handleDelete} loading={deleting}>
              Delete
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* MyVault Agent Chat Widget */}
      {isEditor && <FloatingChatWidget workspaceId={workspaceId} />}
    </Box>
  );
}
